import React from 'react';
import { Box, Divider } from '@wix/design-system';
import type { SupportedAppStatuses } from '@wix/app-market-services-types';
import { isAppWithBenefits } from '@wix/app-market-services';
import {
  appMarketDemoLinkClicked,
  appMarketDeveloperContactBoxActionClick,
} from '@wix/bi-logger-app-market-data/v2';
import {
  AppOverview,
  AppType,
  CompanyInfo,
  QuickInfo,
  VisibilityDetector,
} from '@wix/app-market-components';
import { useQuickInfoActions } from '../app-page-sections/quick-info/quick-info-actions';
import type { getAppPageDataResponse } from '../app-page-api';
import type { AppPageOptions } from '../types';
import { AppPageCTAs } from '../app-page-ctas';
import { appPageDataHooks } from '../data-hooks';
import { useEssentials, useSiteContext } from '@wix/app-market-core';
import { AppProperties } from '../app-page-sections/app-properties';
import s from '../app-page.scss';
import { isCompanyNameClickable } from '../utils/company';
import type { AppPageType } from '@wix/app-market-core-components-types';
import { AppNotificationType } from '../app-page-sections/notifications/types';

interface AppPageInfoProps
  extends Omit<getAppPageDataResponse, 'pricingPlans' | 'permissions'> {
  options?: AppPageOptions;
  status: SupportedAppStatuses;
  isTestMode?: boolean;
  onShowHeader: () => void;
  onHideHeader: () => void;
  onSectionLoad: () => void;
  externalPricingPageUrl?: string;
  appPageType: AppPageType;
}

export function AppPageInfo({
  app,
  status,
  overview,
  quickInfo,
  managedApp,
  properties,
  companyInfo,
  notification,
  options = {},
  onShowHeader,
  onHideHeader,
  traceableTrader,
  onSectionLoad,
  isTestMode,
  externalPricingPageUrl,
  appPageType,
}: AppPageInfoProps) {
  const {
    onTagClick,
    onCompanyClick,
    onReviewsClick,
    onGalleryChange,
    onExpandDescriptionButtonClick,
  } = useQuickInfoActions(appPageType);
  const { isPremium, languageCode } = useSiteContext();
  const { i18n, biLogger, experiments } = useEssentials();

  const {
    showCtas = true,
    showRating = true,
    showHeaderLinks = true,
    showFeaturedIn,
    disabledLinks = [],
  } = options;

  return (
    <>
      <QuickInfo
        media={quickInfo.media}
        app={app}
        isAppCompatible={
          notification?.type !== AppNotificationType.AppIncompatible
        }
        onCompanyClick={
          isCompanyNameClickable({
            disabledLinks,
            status,
          })
            ? (companyName: string, companySlug: string) =>
                onCompanyClick(companyName, companySlug, app.id)
            : undefined
        }
        onReviewsClick={(reviewsAmount: number) =>
          onReviewsClick(app.id, reviewsAmount)
        }
        mainActionButton={
          showCtas && (
            <VisibilityDetector
              onHidden={onShowHeader}
              onVisible={onHideHeader}
            >
              <Box align="center" marginTop={app.isUpgradable ? '6px' : 0}>
                <AppPageCTAs
                  managedApp={managedApp}
                  status={status}
                  app={{
                    id: app.id,
                    instanceId: app.instanceId,
                    name: app.name,
                    isUpgradable: app.isUpgradable,
                    isAppWithBenefits: isAppWithBenefits(app.appBadges),
                    isInstalled: app.installation.isInstalled,
                    isBuiltIn: Boolean(app.installation.isBuiltIn),
                    appBadges: app.appBadges,
                    notification,
                    gTag: app.gTag,
                    externalPricingPageUrl,
                  }}
                  dataHook={appPageDataHooks.APP_PAGE_CTA}
                  size={app.isUpgradable ? 'small' : 'large'}
                  isTestMode={isTestMode}
                  appPageType={appPageType}
                />
              </Box>
            </VisibilityDetector>
          )
        }
        onGalleryChange={(oldIndex: number, newIndex: number) =>
          onGalleryChange(oldIndex, newIndex, app.id)
        }
        languageCode={languageCode}
        experiments={experiments}
        isPremiumSite={isPremium}
        options={{
          showRating,
        }}
      />
      <Box margin="36px 0px">
        <Divider />
      </Box>
      <VisibilityDetector onFirstVisible={onSectionLoad}>
        <Box className={s.mainContent} gap="24px">
          <Box flex={1}>
            {(Boolean(overview.description) ||
              Boolean(overview.benefits?.length)) && (
              <AppOverview
                appName={app.name}
                description={overview.description}
                benefits={overview.benefits}
                demoUrl={overview.demoUrl}
                onClickDemoUrl={() => {
                  biLogger.report(
                    appMarketDemoLinkClicked({
                      app_id: app.id,
                      demo_link: overview.demoUrl,
                      app_page_type: appPageType,
                    }),
                  );
                }}
                onExpandToggleClick={(isExpanded: boolean) =>
                  onExpandDescriptionButtonClick?.(app.id, isExpanded)
                }
                i18n={i18n}
                rows={20}
                size="large"
                showAdaptiveLayout={true}
                showHeading={true}
                collapsible={true}
              />
            )}
          </Box>
          <Box
            className={s.propertiesCompanyContainer}
            direction="vertical"
            gap="24px"
          >
            <AppProperties
              appId={app.id}
              appName={app.name}
              isWixApp={app.type === AppType.WIX_APP}
              geoAvailability={properties.geoAvailability}
              appCollections={properties.appCollections}
              supportedLanguages={properties.supportedLanguages}
              isAppContentTranslatable={properties.isAppContentTranslatable}
              appDependencies={properties.appDependencies}
              subCategories={showHeaderLinks ? quickInfo.subCategories : []}
              onTagClick={(
                categorySlug: string,
                subCategory: string,
                label: 'category' | 'sub_category',
                location: 'side_category_page',
              ) =>
                onTagClick(categorySlug, subCategory, app.id, label, location)
              }
              options={{
                showFeaturedIn,
              }}
            />
            <CompanyInfo
              traceableTrader={traceableTrader}
              appInfo={{
                name: companyInfo.name,
                imageURL: companyInfo.image,
              }}
              website={{
                href: companyInfo.websiteUrl,
                onClick: () => {
                  biLogger.report(
                    appMarketDeveloperContactBoxActionClick({
                      app_id: app.id,
                      actionName: 'visit_website',
                      app_page_type: appPageType,
                    }),
                  );
                },
              }}
              contactSupport={{
                href: companyInfo.contactUs,
                onClick: () => {
                  biLogger.report(
                    appMarketDeveloperContactBoxActionClick({
                      app_id: app.id,
                      actionName: 'contact_support',
                      app_page_type: appPageType,
                    }),
                  );
                },
              }}
              privacyPolicy={{
                href: companyInfo.privacyPolicyUrl,
                onClick: () => {
                  biLogger.report(
                    appMarketDeveloperContactBoxActionClick({
                      app_id: app.id,
                      actionName: 'privacy',
                      app_page_type: appPageType,
                    }),
                  );
                },
              }}
              i18n={i18n}
            />
          </Box>
        </Box>
      </VisibilityDetector>
    </>
  );
}
